/**
 * useVoiceConversationEngine - Reusable voice conversation state machine
 * 
 * Extracted from the Symptom Checker VoiceConversation to be reusable across
 * features (SecureLink check-in, CMR review, discharge summary, etc.).
 * 
 * Handles all generic voice conversation infrastructure:
 * - STT (Web Speech API + Deepgram Flux for English; Deepgram Nova-3 streaming on
 *   browsers where Web Speech is broken, i.e. Microsoft Edge)
 * - TTS (OpenAI via Firebase or Deepgram Aura)
 * - Echo filtering and input gating
 * - Pause detection (timer-based + Flux EndOfTurn)
 * - Conversation log management
 * - Processing mutex for safe async question flow
 * 
 * Supports controlled state: when `controlledConversationLog` / `controlledQuestionsAsked`
 * are provided, the hook uses them instead of internal state (enables persistence
 * across remounts, lifted state in parent).
 * 
 * The "brain" (what questions to ask) is pluggable via onPauseDetected callback.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  deepgramFluxService,
  FLUX_EVENTS,
  deepgramTTSService,
  geminiFlashTTSService,
  deepgramSTTService,
} from 'services/voice';
import {
  speak as openAISpeak,
  stopSpeaking as openAIStopSpeaking,
  setConfig as setOpenAIConfig,
  unlockAudioContext,
  prefetchSpeech,
  playPrefetchedAudio,
} from 'services/ttsService';

import {
  DEFAULT_PAUSE_DURATION_MS,
  FIRST_QUESTION_PAUSE_MS,
  VOICE_CONFIG,
  ECHO_COOLDOWN_MS,
  POST_TTS_WEB_SPEECH_RESUME_MS,
  RESPONSE_DELAY_MS,
  SUPPORTED_LANGUAGES,
  useEchoFilter,
  useSymptomLanguage,
  usePauseDetection,
  useVoiceTTS,
  TTS_PROVIDER,
} from '../../features/patient/components/symptomCheck/voice-conversation';
import { scheduleVerbalEndConversationAck } from './scheduleVerbalEndConversationAck';
import { createVoiceLogAdapter } from './voiceLogAdapter';
import { isWebSpeechSttUnreliable } from './voiceConversationFlags';
import { createDeepgramMicCapture } from './deepgramMicCapture';

/**
 * @param {Object} options
 * @param {function} options.onPauseDetected - Called when user finishes speaking.
 *   Receives ({ userTranscript, conversationLog, questionsAsked, context }).
 *   Must return { question: string|null, isComplete: boolean } or null to skip.
 * @param {string} [options.openingMessage] - First thing the AI says
 * @param {number} [options.pauseDuration] - Pause duration in ms
 * @param {string} [options.language='en-US'] - Language code
 * @param {function} [options.onLanguageChange] - Called when language changes
 * @param {string} [options.ttsProvider='openai'] - TTS provider
 * @param {string} [options.ttsVoice] - TTS voice id for current provider (user preference); used for OpenAI and Deepgram
 * @param {function} [options.onComplete] - Called with (conversationLog) when ended
 * @param {function} [options.onUserSpeech] - Called with each user utterance
 * @param {function} [options.onLogEntry] - Called with (newEntry, fullLog) on each log change
 * @param {function} [options.onLiveTranscript] - Called with (text) for live/in-progress STT (interim + accumulated); call with '' when committed
 * @param {Object} [options.context] - Opaque context passed to onPauseDetected
 * @param {boolean} [options.askFirstQuestionAfterOpening=false] - If true, after the opening message is spoken
 *   the engine immediately requests and speaks the first question (no wait for user response). Used for check-in.
 * @param {boolean} [options.askDisclaimerAfterOpening=true] - If true, the engine speaks the one-time
 *   medicolegal disclaimer from the brain before the opening line, then the opening question.
 * 
 * Controlled state (optional - enables persistence across remounts):
 * @param {Array} [options.controlledConversationLog] - When provided, use this instead of internal log state
 * @param {function} [options.onConversationLogChange] - Setter for controlled log
 * @param {Array} [options.controlledQuestionsAsked] - When provided, use this instead of internal questions state
 * @param {function} [options.onQuestionsAskedChange] - Setter for controlled questions
 */
const useVoiceConversationEngine = ({
  onPauseDetected,
  openingMessage = 'Hello! How can I help you today?',
  pauseDuration = DEFAULT_PAUSE_DURATION_MS,
  language = 'en-US',
  onLanguageChange,
  ttsProvider = 'openai',
  ttsVoice,
  onComplete,
  onUserSpeech,
  onLogEntry,
  onLiveTranscript,
  context,
  askFirstQuestionAfterOpening = false,
  askDisclaimerAfterOpening = true,
  // Controlled state
  controlledConversationLog,
  onConversationLogChange,
  controlledQuestionsAsked,
  onQuestionsAskedChange,
} = {}) => {
  // =========================================================================
  // STATE (supports controlled & uncontrolled modes)
  // =========================================================================
  const [isActive, setIsActive] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState('');

  // Internal state (used when not in controlled mode)
  const [questionsAskedInternal, setQuestionsAskedInternal] = useState([]);
  const [conversationLogInternal, setConversationLogInternal] = useState([]);

  // Resolve controlled vs uncontrolled
  const questionsAsked = controlledQuestionsAsked !== undefined ? controlledQuestionsAsked : questionsAskedInternal;
  const conversationLog = controlledConversationLog !== undefined ? controlledConversationLog : conversationLogInternal;

  const setQuestionsAsked = useCallback((valueOrUpdater) => {
    if (onQuestionsAskedChange) {
      if (typeof valueOrUpdater === 'function') {
        onQuestionsAskedChange(prev => valueOrUpdater(prev));
      } else {
        onQuestionsAskedChange(valueOrUpdater);
      }
    } else {
      setQuestionsAskedInternal(valueOrUpdater);
    }
  }, [onQuestionsAskedChange]);

  const setConversationLog = useCallback((valueOrUpdater) => {
    if (onConversationLogChange) {
      if (typeof valueOrUpdater === 'function') {
        onConversationLogChange(prev => valueOrUpdater(prev));
      } else {
        onConversationLogChange(valueOrUpdater);
      }
    } else {
      setConversationLogInternal(valueOrUpdater);
    }
  }, [onConversationLogChange]);

  // Audio confirmation
  const [audioConfirmed, setAudioConfirmed] = useState(false);
  const audioConfirmedRef = useRef(false);

  // Input gating
  const acceptingInputRef = useRef(true);
  const [isAcceptingInput, setIsAcceptingInput] = useState(true);

  // Deepgram Flux state
  const [isFluxConnected, setIsFluxConnected] = useState(false);
  const [endConversationAcknowledged, setEndConversationAcknowledged] = useState(false);

  // =========================================================================
  // REFS (avoid stale closures)
  // =========================================================================
  const recognitionRef = useRef(null);
  // Edge server-STT (Deepgram Nova-3) path — mic capture session + latest result handler.
  const deepgramCaptureRef = useRef(null);
  const deepgramSttStartingRef = useRef(false);
  const serverSttResultRef = useRef(null);
  const audioRef = useRef(null);
  const prefetchedOpeningAudioRef = useRef(null);
  const prefetchedOpeningTextRef = useRef('');
  const prefetchedOpeningKeyRef = useRef('');
  const isPrefetchingRef = useRef(false);
  const conversationScrollRef = useRef(null);
  const questionsAskedRef = useRef([]);
  const conversationLogRef = useRef([]);
  const handlePauseDetectedRef = useRef(null);
  const askQuestionRef = useRef(null);
  const hasStartedRef = useRef(false);
  const isActiveRef = useRef(false);
  const hasSpokeRecentlyRef = useRef(false);
  const isRecognitionRunningRef = useRef(false);
  const pendingEndOfTurnRef = useRef(null);
  const ttsProviderRef = useRef(ttsProvider);
  const ttsVoiceRef = useRef(ttsVoice);
  const isProcessingPauseRef = useRef(false);
  const adapterRef = useRef(null);
  const addToLogRef = useRef(null);
  /** Deferred user transcript to replay after disclaimer TTS completes. */
  const pendingDeferredTranscriptRef = useRef('');
  /** When set, handlePauseDetected uses this text instead of accumulator. */
  const deferredTranscriptOverrideRef = useRef('');
  const transcriptAccumulatorRef = useRef('');
  const interimTranscriptRef = useRef(''); // Latest interim (e.g. "yes"/"no") when browser never sends isFinal
  const onPauseDetectedRef = useRef(onPauseDetected);
  const onLiveTranscriptRef = useRef(onLiveTranscript);
  const contextRef = useRef(context);
  const openingMessageRef = useRef(openingMessage);
  const stopConversationRef = useRef(null);
  const safetyTimerRef = useRef(null);
  const askFirstQuestionAfterOpeningRef = useRef(askFirstQuestionAfterOpening);
  const askDisclaimerAfterOpeningRef = useRef(askDisclaimerAfterOpening);

  // Keep controlled arrays in refs immediately (useEffect alone runs too late for first commit)
  questionsAskedRef.current = questionsAsked;
  conversationLogRef.current = conversationLog;

  // =========================================================================
  // HOOKS
  // =========================================================================
  const echoFilter = useEchoFilter();
  const symptomLanguage = useSymptomLanguage({
    defaultLanguage: language,
    onLanguageChange,
  });
  const pauseDetection = usePauseDetection({
    pauseDurationMs: pauseDuration,
    onPauseDetected: () => handlePauseDetectedRef.current?.(),
  });
  const { isSpeaking, isSpeakingRef, setSpeaking } = useVoiceTTS();

  // Native Web Speech presence. Chrome exposes AND honors it; Edge exposes it but errors at
  // runtime (`language-not-supported` / `network`) and returns no transcript.
  const nativeWebSpeechAvailable = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
  // INVARIANT: On Edge, capture speech with a server STT (Deepgram Nova-3 streaming) instead
  // of the broken Web Speech API, so the Classic engine keeps working. See voiceConversationFlags.js.
  const useServerStt = isWebSpeechSttUnreliable();
  // Web Speech is the actual transcript source only where the browser can run it (not Edge).
  const useWebSpeech = nativeWebSpeechAvailable && !useServerStt;
  // Flux (English turn detection) layers on the Web Speech path only — never the Edge server-STT path.
  const useDeepgramFlux = symptomLanguage.shouldUseFlux() && !useServerStt;
  // Voice is supported when we can use either Web Speech (Chrome) or server STT (Edge).
  const speechSupported = useServerStt || nativeWebSpeechAvailable;

  // Keep refs in sync with props
  useEffect(() => { ttsProviderRef.current = ttsProvider; }, [ttsProvider]);
  useEffect(() => { ttsVoiceRef.current = ttsVoice; }, [ttsVoice]);
  useEffect(() => { onPauseDetectedRef.current = onPauseDetected; }, [onPauseDetected]);
  useEffect(() => { contextRef.current = context; }, [context]);
  useEffect(() => { openingMessageRef.current = openingMessage; }, [openingMessage]);
  useEffect(() => { onLiveTranscriptRef.current = onLiveTranscript; }, [onLiveTranscript]);
  useEffect(() => { askFirstQuestionAfterOpeningRef.current = askFirstQuestionAfterOpening; }, [askFirstQuestionAfterOpening]);
  useEffect(() => { askDisclaimerAfterOpeningRef.current = askDisclaimerAfterOpening; }, [askDisclaimerAfterOpening]);

  const resolveAssistantTextForUser = useCallback(async (text) => {
    const baseText = String(text || '').trim();
    if (!baseText) return '';
    if (!symptomLanguage.needsTranslation) return baseText;
    try {
      return await symptomLanguage.translateQuestion(baseText);
    } catch (err) {
      console.warn('[VoiceEngine] Translation for assistant text failed:', err);
      return baseText;
    }
  }, [symptomLanguage]);

  // =========================================================================
  // PREFETCH OPENING AUDIO
  // =========================================================================
  useEffect(() => {
    const doPrefetch = async () => {
      const voice = ttsVoiceRef.current ?? VOICE_CONFIG.voice;
      const prefetchKey = [
        ttsProvider,
        voice || '',
        openingMessageRef.current || '',
        symptomLanguage.language || 'en-US',
      ].join('|');
      if (prefetchedOpeningKeyRef.current !== prefetchKey) {
        prefetchedOpeningAudioRef.current = null;
        prefetchedOpeningTextRef.current = '';
        prefetchedOpeningKeyRef.current = prefetchKey;
      }
      if (prefetchedOpeningAudioRef.current || isPrefetchingRef.current) return;
      isPrefetchingRef.current = true;
      try {
        const text = await resolveAssistantTextForUser(openingMessageRef.current);
        if (!text) { isPrefetchingRef.current = false; return; }
        const speed = VOICE_CONFIG.speed ?? 0.95;
        if (ttsProvider === 'openai') {
          setOpenAIConfig({ voice, speed });
          const audioBlob = await prefetchSpeech(text);
          if (audioBlob) {
            prefetchedOpeningAudioRef.current = audioBlob;
            prefetchedOpeningTextRef.current = text;
          }
        } else if (ttsProvider === 'deepgram') {
          deepgramTTSService.configure({ voice });
          await deepgramTTSService.generateSpeech(text, { voice, useCache: true });
        } else if (ttsProvider === 'gemini_flash_tts') {
          geminiFlashTTSService.configure({
            apiKey: process.env.REACT_APP_GOOGLE_AI_API_KEY,
            voiceName: voice || 'Aoede',
          });
          await geminiFlashTTSService.generateSpeech(text, { voiceName: voice || 'Aoede' });
        }
      } catch (error) {
        console.warn('[VoiceEngine] Failed to prefetch opening audio:', error);
      } finally {
        isPrefetchingRef.current = false;
      }
    };
    doPrefetch();
  }, [ttsProvider, ttsVoice, openingMessage, symptomLanguage.language, resolveAssistantTextForUser]);

  // =========================================================================
  // PRE-CONNECT DEEPGRAM FLUX
  // =========================================================================
  useEffect(() => {
    if (!useDeepgramFlux) return;
    const preConnect = async () => {
      if (deepgramFluxService.isConnected()) { setIsFluxConnected(true); return; }
      try {
        const connected = await deepgramFluxService.connect();
        if (connected) setIsFluxConnected(true);
      } catch (error) {
        console.warn('[VoiceEngine] Flux pre-connection error:', error);
      }
    };
    preConnect();
  }, [useDeepgramFlux]);

  // =========================================================================
  // CONVERSATION LOG
  // =========================================================================

  const addToLog = useCallback((speaker, text) => {
    const newEntry = { speaker, text, timestamp: Date.now() };
    const currentLog = conversationLogRef.current;
    const newLog = [...currentLog, newEntry];
    conversationLogRef.current = newLog;
    setConversationLog(newLog);

    // Notify consumer of new entry
    setTimeout(() => {
      onLogEntry?.(newEntry, newLog);
    }, 0);
  }, [setConversationLog, onLogEntry]);

  addToLogRef.current = addToLog;

  if (!adapterRef.current) {
    adapterRef.current = createVoiceLogAdapter({
      onCommit: (entry) => {
        addToLogRef.current?.(entry.speaker, entry.text);
      },
      onLivePartial: () => {
        // Classic has no streaming partials — no-op
      },
      onBeforeCommit: () => {
        // No DDX dirty flag needed in Classic
      },
    });
  }

  const addUserToLog = useCallback(async (userText) => {
    if (!userText || !userText.trim()) return;
    const toLog = symptomLanguage.needsTranslation
      ? await symptomLanguage.translateToEnglish(userText)
      : userText.trim();
    adapterRef.current.commitUserTurn(toLog);
    setTimeout(() => { onUserSpeech?.(toLog); }, 0);
  }, [symptomLanguage, onUserSpeech]);

  // =========================================================================
  // TTS
  // =========================================================================

  const speakWithPreferredProvider = useCallback(async (text, options = {}) => {
    const provider = ttsProviderRef.current;
    const preferredVoice = ttsVoiceRef.current ?? VOICE_CONFIG.voice;
    const preferredSpeed = VOICE_CONFIG.speed ?? 0.95;
    const {
      onStart,
      onEnd,
      onError,
      voice = preferredVoice,
      speed = preferredSpeed,
      resolvedText,
    } = options;

    if (!text || text.trim().length === 0) {
      console.warn('[VoiceEngine] Empty text passed to TTS');
      onEnd?.();
      return null;
    }

    const textToSpeak =
      typeof resolvedText === 'string' && resolvedText.trim()
        ? resolvedText.trim()
        : await resolveAssistantTextForUser(text);

    echoFilter.addSystemUtterance(textToSpeak);
    acceptingInputRef.current = false;
    setIsAcceptingInput(false);

    const langCode = symptomLanguage.language || 'en-US';

    const handleTTSStart = () => {
      echoFilter.startEchoCooldown();
      onStart?.();
    };

    const handleTTSEnd = () => {
      echoFilter.endEchoCooldown(ECHO_COOLDOWN_MS);
      setTimeout(async () => {
        acceptingInputRef.current = true;
        setIsAcceptingInput(true);
        if (pendingEndOfTurnRef.current && hasSpokeRecentlyRef.current && isActiveRef.current) {
          const pending = pendingEndOfTurnRef.current;
          pendingEndOfTurnRef.current = null;
          if (pending.transcript) await addUserToLog(pending.transcript);
          if (handlePauseDetectedRef.current) handlePauseDetectedRef.current();
        }
      }, ECHO_COOLDOWN_MS);
      onEnd?.();
    };

    const handleTTSError = (err) => {
      echoFilter.endEchoCooldown(ECHO_COOLDOWN_MS);
      setTimeout(async () => {
        acceptingInputRef.current = true;
        setIsAcceptingInput(true);
        if (pendingEndOfTurnRef.current && hasSpokeRecentlyRef.current && isActiveRef.current) {
          const pending = pendingEndOfTurnRef.current;
          pendingEndOfTurnRef.current = null;
          if (pending.transcript) await addUserToLog(pending.transcript);
          if (handlePauseDetectedRef.current) handlePauseDetectedRef.current();
        }
      }, ECHO_COOLDOWN_MS);
      onError?.(err);
    };

    if (provider === TTS_PROVIDER.DEEPGRAM) {
      try {
        deepgramTTSService.configure({ voice });
        deepgramTTSService.setHandlers({
          onStart: handleTTSStart,
          onEnd: handleTTSEnd,
          onError: (err) => { console.error('[VoiceEngine] Deepgram TTS error:', err); handleTTSError(err); },
        });
        await deepgramTTSService.speak(textToSpeak, { voice });
        return { provider: 'deepgram' };
      } catch (error) {
        console.error('[VoiceEngine] Deepgram TTS error:', error);
        handleTTSError(error);
        throw error;
      }
    }

    if (provider === TTS_PROVIDER.GEMINI_FLASH_TTS) {
      try {
        geminiFlashTTSService.configure({
          apiKey: process.env.REACT_APP_GOOGLE_AI_API_KEY,
          voiceName: voice || 'Aoede',
        });
        geminiFlashTTSService.setHandlers({
          onStart: handleTTSStart,
          onEnd: handleTTSEnd,
          onError: (err) => { console.error('[VoiceEngine] Gemini Flash TTS error:', err); handleTTSError(err); },
        });
        await geminiFlashTTSService.speak(textToSpeak, { voiceName: voice || 'Aoede' });
        return { provider: 'gemini_flash_tts' };
      } catch (error) {
        console.error('[VoiceEngine] Gemini Flash TTS error:', error);
        handleTTSError(error);
        throw error;
      }
    }

    try {
      setOpenAIConfig({ voice, speed });
      await openAISpeak(textToSpeak, langCode, {
        onStart: handleTTSStart,
        onEnd: handleTTSEnd,
        onError: (err) => { console.error('[VoiceEngine] OpenAI TTS error:', err); handleTTSError(err); },
      });
      return { provider: 'openai' };
    } catch (error) {
      console.error('[VoiceEngine] OpenAI TTS error:', error);
      handleTTSError(error);
      throw error;
    }
  }, [echoFilter, symptomLanguage.language, addUserToLog, resolveAssistantTextForUser]);

  // =========================================================================
  // SPEECH RECOGNITION (Web Speech API)
  // =========================================================================

  useEffect(() => {
    // Only create Web Speech recognition where the browser can actually run it.
    // On Edge (useServerStt) this is skipped — the Deepgram streaming path handles STT.
    if (!useWebSpeech) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = symptomLanguage.getWebSpeechLanguage();

    recognition.onstart = () => { isRecognitionRunningRef.current = true; };
    recognition.onaudiostart = () => {
      setAudioConfirmed(true);
      audioConfirmedRef.current = true;
    };
    recognition.onspeechstart = () => {
      if (!audioConfirmedRef.current) {
        setAudioConfirmed(true);
        audioConfirmedRef.current = true;
      }
    };

    recognition.onresult = (event) => {
      if (!acceptingInputRef.current) return;
      if (isSpeakingRef.current) return;
      if (echoFilter.isInCooldown()) return;
      if (isProcessingPauseRef.current) return;

      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript?.trim() || '';
        if (result.isFinal) {
          finalTranscript += transcript;
        }
        // Keep latest interim (or final) so short yes/no that never go final are still captured on pause
        if (transcript) {
          interimTranscriptRef.current = transcript;
        }
      }
      if (event.results.length > 0 && event.results[event.results.length - 1].isFinal) {
        interimTranscriptRef.current = '';
      }

      if (finalTranscript || event.results.length > 0) {
        pauseDetection.clearPauseTimer();

        if (finalTranscript) {
          transcriptAccumulatorRef.current = (transcriptAccumulatorRef.current + ' ' + finalTranscript).trim();
          hasSpokeRecentlyRef.current = true;
        } else if (interimTranscriptRef.current) {
          hasSpokeRecentlyRef.current = true;
        }

        const liveText = (transcriptAccumulatorRef.current + (interimTranscriptRef.current ? ' ' + interimTranscriptRef.current : '')).trim();
        onLiveTranscriptRef.current?.(liveText);

        const currentPauseDuration = questionsAskedRef.current.length === 0
          ? FIRST_QUESTION_PAUSE_MS
          : pauseDuration;
        pauseDetection.startPauseTimer(currentPauseDuration);
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech' || event.error === 'aborted') {
        if (event.error === 'aborted') isRecognitionRunningRef.current = false;
      } else {
        console.error('[VoiceEngine] Recognition error:', event.error);
        setIsListening(false);
        isRecognitionRunningRef.current = false;
      }
    };

    recognition.onend = () => {
      isRecognitionRunningRef.current = false;
      if (isActiveRef.current) {
        setTimeout(() => {
          // Never restart capture while assistant TTS is playing — simultaneous mic + speaker
          // causes many browsers to duck or clip TTS (often after the first sentence).
          if (
            isActiveRef.current &&
            !isRecognitionRunningRef.current &&
            recognitionRef.current &&
            !isSpeakingRef.current
          ) {
            try { recognitionRef.current.start(); } catch (e) { /* ignore */ }
          }
        }, 100);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      pauseDetection.clearPauseTimer();
      if (recognitionRef.current) {
        try {
          recognitionRef.current.onend = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onresult = null;
          recognitionRef.current.stop();
        } catch (e) { /* ignore */ }
        recognitionRef.current = null;
      }
      isRecognitionRunningRef.current = false;
    };
  }, [useWebSpeech, pauseDuration, symptomLanguage.language]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // DEEPGRAM FLUX (English alternative to Web Speech API)
  // =========================================================================

  useEffect(() => {
    if (!useDeepgramFlux) return;

    const handleFluxTranscript = (data) => {
      if (!data.transcript) return;
      if (isSpeakingRef.current || !acceptingInputRef.current) return;
      if (echoFilter.isInCooldown()) return;
      if (isProcessingPauseRef.current) return;

      if (!audioConfirmedRef.current) {
        setAudioConfirmed(true);
        audioConfirmedRef.current = true;
      }

      // Accumulate; echo filter happens when end-of-turn fires
      transcriptAccumulatorRef.current = (transcriptAccumulatorRef.current + ' ' + data.transcript).trim();
      hasSpokeRecentlyRef.current = true;
      onLiveTranscriptRef.current?.(transcriptAccumulatorRef.current.trim());
    };

    const handleFluxEndOfTurn = async (data) => {
      if (isSpeakingRef.current) return;
      if (isProcessingPauseRef.current) return;
      if (!acceptingInputRef.current) {
        if (hasSpokeRecentlyRef.current) {
          pendingEndOfTurnRef.current = { timestamp: Date.now(), transcript: data.transcript };
        }
        return;
      }
      if (hasSpokeRecentlyRef.current && isActiveRef.current) {
        // Flush accumulated transcript through echo filter before processing
        const accumulated = transcriptAccumulatorRef.current || data.transcript || '';
        transcriptAccumulatorRef.current = '';
        interimTranscriptRef.current = '';
        onLiveTranscriptRef.current?.('');
        const cleaned = echoFilter.filterEcho(accumulated);
        if (cleaned && cleaned.trim()) {
          await addUserToLog(cleaned.trim());
        }
        if (handlePauseDetectedRef.current) handlePauseDetectedRef.current();
      }
    };

    const handleFluxTurnResumed = () => { /* user continued speaking */ };
    const handleFluxConnectionChange = (state) => { setIsFluxConnected(state === 'connected'); };

    deepgramFluxService.on(FLUX_EVENTS.TRANSCRIPT, handleFluxTranscript);
    deepgramFluxService.on(FLUX_EVENTS.END_OF_TURN, handleFluxEndOfTurn);
    deepgramFluxService.on(FLUX_EVENTS.TURN_RESUMED, handleFluxTurnResumed);
    deepgramFluxService.on('connectionStateChange', handleFluxConnectionChange);

    return () => {
      deepgramFluxService.off(FLUX_EVENTS.TRANSCRIPT, handleFluxTranscript);
      deepgramFluxService.off(FLUX_EVENTS.END_OF_TURN, handleFluxEndOfTurn);
      deepgramFluxService.off(FLUX_EVENTS.TURN_RESUMED, handleFluxTurnResumed);
      deepgramFluxService.off('connectionStateChange', handleFluxConnectionChange);
      if (deepgramFluxService.isConnected()) deepgramFluxService.disconnect();
    };
  }, [useDeepgramFlux, addUserToLog]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // SERVER STT (Deepgram Nova-3 streaming) — Classic STT path for Edge
  // =========================================================================

  // Reassigned every render so the Deepgram handlers (set once at connect time) always see
  // current closures. Mirrors the Web Speech `onresult` flow: same input gating, transcript
  // accumulation, live-transcript callback, and timer-based pause detection — so the rest of
  // the engine (pause handler → ask next question) behaves identically regardless of STT source.
  serverSttResultRef.current = (result, isFinal) => {
    if (!acceptingInputRef.current) return;
    if (isSpeakingRef.current) return;
    if (echoFilter.isInCooldown()) return;
    if (isProcessingPauseRef.current) return;

    const text = String(result?.text || '').trim();
    if (!text) return;

    if (!audioConfirmedRef.current) {
      setAudioConfirmed(true);
      audioConfirmedRef.current = true;
    }

    pauseDetection.clearPauseTimer();

    if (isFinal) {
      transcriptAccumulatorRef.current = (transcriptAccumulatorRef.current + ' ' + text).trim();
      interimTranscriptRef.current = '';
    } else {
      // Keep latest interim so short yes/no that never go final are still captured on pause.
      interimTranscriptRef.current = text;
    }
    hasSpokeRecentlyRef.current = true;

    const liveText = (transcriptAccumulatorRef.current + (interimTranscriptRef.current ? ' ' + interimTranscriptRef.current : '')).trim();
    onLiveTranscriptRef.current?.(liveText);

    const currentPauseDuration = questionsAskedRef.current.length === 0
      ? FIRST_QUESTION_PAUSE_MS
      : pauseDuration;
    pauseDetection.startPauseTimer(currentPauseDuration);
  };

  // Open the mic and stream PCM to Deepgram Nova-3. Used only when useServerStt (Edge).
  const startServerStt = useCallback(async () => {
    if (deepgramSttStartingRef.current) return false;
    // Already running from an earlier start (e.g. resume) — just confirm audio.
    if (deepgramSTTService.isConnected && deepgramCaptureRef.current) {
      setAudioConfirmed(true);
      audioConfirmedRef.current = true;
      return true;
    }

    deepgramSttStartingRef.current = true;
    try {
      const langBase = String(symptomLanguage.language || 'en-US').toLowerCase();
      // Nova-3: 'en-US' for English; 'multi' (auto language detection / code-switching) otherwise.
      const dgLanguage = langBase.startsWith('en') ? 'en-US' : 'multi';
      deepgramSTTService.configure({ language: dgLanguage, enableCodeSwitching: true });
      deepgramSTTService.setHandlers({
        onTranscript: (r) => serverSttResultRef.current?.(r, true),
        onInterimTranscript: (r) => serverSttResultRef.current?.(r, false),
        onError: (e) => { console.warn('[VoiceEngine] Deepgram STT error:', e); },
        onConnectionChange: () => { /* status handled via audioConfirmed */ },
      });

      const connected = await deepgramSTTService.connect();
      if (!connected) {
        console.warn('[VoiceEngine] Deepgram STT could not connect (Edge STT unavailable).');
        deepgramSttStartingRef.current = false;
        return false;
      }

      if (!deepgramCaptureRef.current) {
        deepgramCaptureRef.current = createDeepgramMicCapture({
          onAudioData: (buffer) => {
            if (!isActiveRef.current) return;
            // Don't stream while the assistant speaks — prevents transcribing our own TTS.
            if (!acceptingInputRef.current || isSpeakingRef.current) return;
            if (deepgramSTTService.isConnected) deepgramSTTService.sendAudio(buffer);
          },
        });
      }
      await deepgramCaptureRef.current.start();

      setAudioConfirmed(true);
      audioConfirmedRef.current = true;
      deepgramSttStartingRef.current = false;
      return true;
    } catch (error) {
      console.warn('[VoiceEngine] Failed to start Deepgram STT (Edge):', error);
      deepgramSttStartingRef.current = false;
      return false;
    }
  }, [symptomLanguage.language, isSpeakingRef]);

  // Release the mic and Deepgram socket for the server-STT path.
  const stopServerStt = useCallback(() => {
    try {
      deepgramCaptureRef.current?.stop();
    } catch (e) {
      console.warn('[VoiceEngine] Deepgram capture stop failed:', e);
    }
    deepgramCaptureRef.current = null;
    try {
      if (deepgramSTTService.isConnected) deepgramSTTService.disconnect();
    } catch (e) {
      console.warn('[VoiceEngine] Deepgram STT disconnect failed:', e);
    }
  }, []);

  // Always release the mic/socket on unmount.
  useEffect(() => () => stopServerStt(), [stopServerStt]);

  // =========================================================================
  // PAUSE HANDLER (delegates to consumer's onPauseDetected)
  // =========================================================================

  const handlePauseDetected = useCallback(async () => {
    if (isProcessingPauseRef.current) return;
    if (!hasSpokeRecentlyRef.current) return;

    isProcessingPauseRef.current = true;
    try {
      let latestUserUtterance = '';
      const deferredOverride = deferredTranscriptOverrideRef.current;
      if (deferredOverride) {
        deferredTranscriptOverrideRef.current = '';
        latestUserUtterance = deferredOverride;
        // Skip echo filter — deferred text was captured before TTS, not during
      } else {
        // Flush accumulated transcript (final results); fall back to interim for short yes/no that never go final
        let rawAccumulated = transcriptAccumulatorRef.current;
        transcriptAccumulatorRef.current = '';
        if (!rawAccumulated && interimTranscriptRef.current) {
          rawAccumulated = interimTranscriptRef.current;
          interimTranscriptRef.current = '';
        }
        if (rawAccumulated) {
          const cleaned = echoFilter.filterEcho(rawAccumulated);
          if (cleaned && cleaned.trim()) {
            latestUserUtterance = cleaned.trim();
            adapterRef.current.commitUserTurn(latestUserUtterance);
          } else {
            // Entire accumulated text was echo — nothing real to process
            onLiveTranscriptRef.current?.('');
            isProcessingPauseRef.current = false;
            return;
          }
        }
      }
      onLiveTranscriptRef.current?.('');

      const handler = onPauseDetectedRef.current;
      if (!handler) { isProcessingPauseRef.current = false; return; }

      const result = await handler({
        userTranscript: latestUserUtterance,
        conversationLog: conversationLogRef.current,
        questionsAsked: questionsAskedRef.current,
        context: contextRef.current,
      });

      const deferredTranscript = String(result?.deferredUserTranscript || '').trim();
      if (deferredTranscript) {
        pendingDeferredTranscriptRef.current = deferredTranscript;
      }

      if (!result) { isProcessingPauseRef.current = false; return; }

      const replayDeferredBrainTurnAfterTts = async () => {
        const deferredAfterTts = pendingDeferredTranscriptRef.current;
        if (!deferredAfterTts || !isActiveRef.current) return;
        pendingDeferredTranscriptRef.current = '';
        adapterRef.current.replayDeferredUserTurn(deferredAfterTts);
        deferredTranscriptOverrideRef.current = deferredAfterTts;
        hasSpokeRecentlyRef.current = true;
        isProcessingPauseRef.current = false;
        if (handlePauseDetectedRef.current) {
          await handlePauseDetectedRef.current();
        }
      };

      if (result.isComplete) {
        if (result.stopImmediately) {
          scheduleVerbalEndConversationAck(setEndConversationAcknowledged, () => {
            if (stopConversationRef.current) stopConversationRef.current();
          });
          isProcessingPauseRef.current = false;
          return;
        }
        if (result.question) {
          await new Promise(resolve => setTimeout(resolve, RESPONSE_DELAY_MS));
          if (askQuestionRef.current) await askQuestionRef.current(result.question);
          await replayDeferredBrainTurnAfterTts();
        }
        // Auto-stop the conversation on completion
        if (stopConversationRef.current) stopConversationRef.current();
        isProcessingPauseRef.current = false;
        return;
      }

      if (result.question) {
        await new Promise(resolve => setTimeout(resolve, RESPONSE_DELAY_MS));
        if (askQuestionRef.current) await askQuestionRef.current(result.question);
        await replayDeferredBrainTurnAfterTts();
      } else {
        // No question to speak — release mutex immediately
        isProcessingPauseRef.current = false;
      }
    } catch (err) {
      console.error('[VoiceEngine] handlePauseDetected error:', err);
      isProcessingPauseRef.current = false;
    }
  }, [echoFilter]);

  // Keep refs in sync with state
  useEffect(() => {
    addToLogRef.current = addToLog;
    questionsAskedRef.current = questionsAsked;
    handlePauseDetectedRef.current = handlePauseDetected;
  });
  useEffect(() => {
    conversationLogRef.current = conversationLog;
  }, [conversationLog]);

  // =========================================================================
  // ASK QUESTION
  // =========================================================================

  const askQuestion = useCallback(async (question, askOptions = {}) => {
    const resumeListeningAfterSpeak = askOptions.resumeListeningAfterSpeak !== false;
    setSpeaking(true);
    setIsListening(false);
    hasSpokeRecentlyRef.current = false;
    const resolvedQuestion = await resolveAssistantTextForUser(question);
    setCurrentQuestion(resolvedQuestion || question);
    pauseDetection.clearPauseTimer();

    // Stop Web Speech capture during assistant TTS. Leaving the mic open while the
    // same device plays TTS often triggers AEC / duplex behavior that clips audio
    // after the first phrase (e.g. closing line cut after the first sentence).
    if (!useDeepgramFlux && recognitionRef.current && isRecognitionRunningRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        /* ignore */
      }
      isRecognitionRunningRef.current = false;
    }

    const newQuestionsAsked = [...questionsAskedRef.current, question];
    questionsAskedRef.current = newQuestionsAsked;
    setQuestionsAsked(newQuestionsAsked);

    // Clear any previous safety timer (e.g. from a prior askQuestion call)
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
    // Safety timeout: release mutex if TTS gets stuck (15 seconds)
    safetyTimerRef.current = setTimeout(() => {
      safetyTimerRef.current = null;
      if (isProcessingPauseRef.current) {
        console.warn('[VoiceEngine] Safety timeout — releasing mutex after 15s');
        isProcessingPauseRef.current = false;
      }
    }, 15000);

    try {
      await speakWithPreferredProvider(question, {
        resolvedText: resolvedQuestion,
        onEnd: () => {
          if (safetyTimerRef.current) {
            clearTimeout(safetyTimerRef.current);
            safetyTimerRef.current = null;
          }
          setCurrentQuestion('');
          hasSpokeRecentlyRef.current = false;
          pendingEndOfTurnRef.current = null;
          transcriptAccumulatorRef.current = '';
          interimTranscriptRef.current = '';
          onLiveTranscriptRef.current?.('');
          setSpeaking(false);
          isProcessingPauseRef.current = false;
          if (resumeListeningAfterSpeak) {
            setIsListening(true);
            if (!useDeepgramFlux && isActiveRef.current) {
              setTimeout(() => {
                if (!isActiveRef.current || isSpeakingRef.current) return;
                if (recognitionRef.current && !isRecognitionRunningRef.current) {
                  try {
                    recognitionRef.current.start();
                  } catch (e) {
                    /* ignore */
                  }
                }
              }, POST_TTS_WEB_SPEECH_RESUME_MS);
            }
          }
        },
        onError: () => {
          if (safetyTimerRef.current) {
            clearTimeout(safetyTimerRef.current);
            safetyTimerRef.current = null;
          }
          setSpeaking(false);
          setCurrentQuestion('');
          isProcessingPauseRef.current = false;
          if (resumeListeningAfterSpeak) {
            setIsListening(true);
            if (!useDeepgramFlux && isActiveRef.current) {
              setTimeout(() => {
                if (!isActiveRef.current || isSpeakingRef.current) return;
                if (recognitionRef.current && !isRecognitionRunningRef.current) {
                  try {
                    recognitionRef.current.start();
                  } catch (e) {
                    /* ignore */
                  }
                }
              }, POST_TTS_WEB_SPEECH_RESUME_MS);
            }
          }
        },
      });
      adapterRef.current.seedAssistantTurn(resolvedQuestion || question);
      adapterRef.current.commitAssistantTurn(resolvedQuestion || question);
    } catch (error) {
      console.error('[VoiceEngine] Failed to ask question:', error);
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
      setSpeaking(false);
      setCurrentQuestion('');
      isProcessingPauseRef.current = false;
      if (resumeListeningAfterSpeak && !useDeepgramFlux && isActiveRef.current) {
        setTimeout(() => {
          if (!isActiveRef.current || isSpeakingRef.current) return;
          if (recognitionRef.current && !isRecognitionRunningRef.current) {
            try {
              recognitionRef.current.start();
            } catch (e) {
              /* ignore */
            }
          }
        }, POST_TTS_WEB_SPEECH_RESUME_MS);
      }
    }
  }, [
    resolveAssistantTextForUser,
    speakWithPreferredProvider,
    pauseDetection,
    setSpeaking,
    setQuestionsAsked,
    useDeepgramFlux,
    isSpeakingRef,
  ]);

  askQuestionRef.current = askQuestion;

  // Request first question immediately after opening (no user response required). Used for check-in.
  const requestFirstQuestionAfterOpening = useCallback(async () => {
    if (isProcessingPauseRef.current) return;
    const handler = onPauseDetectedRef.current;
    if (!handler) return;

    isProcessingPauseRef.current = true;
    try {
      const result = await handler({
        userTranscript: '',
        conversationLog: conversationLogRef.current,
        questionsAsked: questionsAskedRef.current,
        context: contextRef.current,
      });
      if (result?.question) {
        await new Promise(resolve => setTimeout(resolve, RESPONSE_DELAY_MS));
        if (askQuestionRef.current) await askQuestionRef.current(result.question);
      }
    } catch (err) {
      console.error('[VoiceEngine] requestFirstQuestionAfterOpening error:', err);
    } finally {
      isProcessingPauseRef.current = false;
    }
  }, []);

  const requestOpeningDisclaimer = useCallback(async ({ resumeListeningAfterSpeak = true } = {}) => {
    if (!askDisclaimerAfterOpeningRef.current) return;
    if (isProcessingPauseRef.current) return;
    const handler = onPauseDetectedRef.current;
    if (!handler) return;

    isProcessingPauseRef.current = true;
    try {
      const result = await handler({
        userTranscript: '',
        conversationLog: conversationLogRef.current,
        questionsAsked: questionsAskedRef.current,
        context: contextRef.current,
      });
      if (result?.question) {
        await new Promise(resolve => setTimeout(resolve, RESPONSE_DELAY_MS));
        if (askQuestionRef.current) {
          await askQuestionRef.current(result.question, { resumeListeningAfterSpeak });
        }
      }
    } catch (err) {
      console.error('[VoiceEngine] requestOpeningDisclaimer error:', err);
    } finally {
      isProcessingPauseRef.current = false;
    }
  }, []);

  // Clear safety timer on unmount (Leak-Proof)
  useEffect(() => {
    return () => {
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
    };
  }, []);

  // =========================================================================
  // START / STOP
  // =========================================================================

  const startConversation = useCallback(async () => {
    unlockAudioContext();
    if (isActiveRef.current || hasStartedRef.current) return;

    hasStartedRef.current = true;
    setEndConversationAcknowledged(false);
    setIsActive(true);
    isActiveRef.current = true;
    setIsStarting(true);

    // Check if resuming existing conversation (controlled state has data)
    const hasExistingConversation = questionsAsked.length > 0 || conversationLog.length > 0;

    // Initialize Deepgram Flux if needed
    if (useDeepgramFlux) {
      if (deepgramFluxService.isConnected()) {
        setIsFluxConnected(true);
      } else {
        try {
          const connected = await deepgramFluxService.connect();
          if (connected) setIsFluxConnected(true);
        } catch (error) {
          console.warn('[VoiceEngine] Flux connection error:', error);
        }
      }
    }

    // Only bail when we intend to use Web Speech but it isn't available. On Edge
    // (useServerStt) there is no recognition object — the Deepgram path handles STT.
    if (!useDeepgramFlux && !useServerStt && !recognitionRef.current) {
      hasStartedRef.current = false;
      setIsActive(false);
      isActiveRef.current = false;
      return;
    }

    if (hasExistingConversation) {
      // RESUMING - don't reset state, don't speak opening prompt
      questionsAskedRef.current = questionsAsked;
      setSpeaking(false);
      acceptingInputRef.current = true;
      setIsAcceptingInput(true);

      if (useServerStt) {
        // Edge: resume capturing via Deepgram streaming.
        await startServerStt();
        setIsListening(true);
      } else if (recognitionRef.current) {
        try {
          if (isRecognitionRunningRef.current) {
            setIsListening(true);
          } else {
            recognitionRef.current.start();
            setIsListening(true);
          }
        } catch (e) {
          console.error('[VoiceEngine] Failed to resume listening:', e);
          isRecognitionRunningRef.current = false;
        }
      }
      setIsStarting(false);
      return;
    }

    // FRESH START - reset state and speak disclaimer (if any) then opening prompt
    setQuestionsAsked([]);
    questionsAskedRef.current = [];
    setConversationLog([]);
    conversationLogRef.current = [];
    acceptingInputRef.current = false;
    setIsAcceptingInput(false);

    // Pre-start STT capture
    if (useServerStt) {
      // Edge: open the mic and Deepgram socket now (still within the Start-button gesture).
      await startServerStt();
    } else if (recognitionRef.current && !isRecognitionRunningRef.current) {
      try { recognitionRef.current.start(); } catch (e) { /* ignore */ }
      setTimeout(() => {
        if (isActiveRef.current && !audioConfirmedRef.current && isRecognitionRunningRef.current) {
          setAudioConfirmed(true);
          audioConfirmedRef.current = true;
        }
      }, 3000);
    }

    // Same as askQuestion: do not capture while assistant TTS plays (avoids AEC clipping).
    if (!useDeepgramFlux && recognitionRef.current && isRecognitionRunningRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        /* ignore */
      }
      isRecognitionRunningRef.current = false;
    }

    if (askDisclaimerAfterOpeningRef.current) {
      await requestOpeningDisclaimer({ resumeListeningAfterSpeak: false });
      if (!useDeepgramFlux && recognitionRef.current && isRecognitionRunningRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          /* ignore */
        }
        isRecognitionRunningRef.current = false;
      }
    }

    // Speak opening
    const openingText = openingMessageRef.current;
    const openingTextForUser = await resolveAssistantTextForUser(openingText);
    setSpeaking(true);
    adapterRef.current.commitAssistantTurn(openingTextForUser || openingText);

    const startRecognitionAfterTTS = () => {
      setSpeaking(false);
      pendingEndOfTurnRef.current = null;
      // Edge: Deepgram capture runs continuously; just resume the listening UI state.
      if (useServerStt) { setIsListening(true); return; }
      if (!recognitionRef.current) return;
      const doStart = () => {
        try {
          if (isRecognitionRunningRef.current) {
            setIsListening(true);
          } else {
            recognitionRef.current.start();
            setIsListening(true);
          }
        } catch (e) {
          isRecognitionRunningRef.current = false;
        }
      };
      if (useDeepgramFlux) {
        doStart();
      } else {
        setTimeout(doStart, POST_TTS_WEB_SPEECH_RESUME_MS);
      }
    };

    const speakWithBrowserFallback = (text) => {
      if (!('speechSynthesis' in window)) {
        setIsStarting(false);
        acceptingInputRef.current = true;
        setIsAcceptingInput(true);
        startRecognitionAfterTTS();
        return;
      }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.onstart = () => { setIsStarting(false); setSpeaking(true); };
      utterance.onend = () => {
        setTimeout(async () => {
          acceptingInputRef.current = true;
          setIsAcceptingInput(true);
          if (askFirstQuestionAfterOpeningRef.current) {
            await requestFirstQuestionAfterOpening();
          }
          startRecognitionAfterTTS();
        }, 1000);
      };
      utterance.onerror = () => {
        setIsStarting(false);
        acceptingInputRef.current = true;
        setIsAcceptingInput(true);
        startRecognitionAfterTTS();
      };
      window.speechSynthesis.speak(utterance);
    };

    let fallbackStarted = false;
    try {
      if (
        prefetchedOpeningAudioRef.current &&
        ttsProviderRef.current === 'openai' &&
        prefetchedOpeningTextRef.current === (openingTextForUser || openingText)
      ) {
        echoFilter.addSystemUtterance(openingTextForUser || openingText);
        acceptingInputRef.current = false;
        setIsAcceptingInput(false);
        echoFilter.startEchoCooldown();

        await playPrefetchedAudio(prefetchedOpeningAudioRef.current, {
          onStart: () => { setIsStarting(false); setSpeaking(true); },
          onEnd: () => {
            echoFilter.endEchoCooldown(ECHO_COOLDOWN_MS);
            setTimeout(async () => {
              acceptingInputRef.current = true;
              setIsAcceptingInput(true);
            }, ECHO_COOLDOWN_MS);
            void (async () => {
              if (askFirstQuestionAfterOpeningRef.current) {
                await requestFirstQuestionAfterOpening();
              }
              startRecognitionAfterTTS();
            })();
          },
          onError: () => {
            echoFilter.endEchoCooldown(ECHO_COOLDOWN_MS);
            setTimeout(() => { acceptingInputRef.current = true; setIsAcceptingInput(true); }, ECHO_COOLDOWN_MS);
            if (!fallbackStarted) { fallbackStarted = true; speakWithBrowserFallback(openingTextForUser || openingText); }
          },
        });
        prefetchedOpeningAudioRef.current = null;
        prefetchedOpeningTextRef.current = '';
      } else {
        await speakWithPreferredProvider(openingText, {
          resolvedText: openingTextForUser,
          onStart: () => { setIsStarting(false); setSpeaking(true); },
          onEnd: () => {
            void (async () => {
              if (askFirstQuestionAfterOpeningRef.current) {
                await requestFirstQuestionAfterOpening();
              }
              startRecognitionAfterTTS();
            })();
          },
          onError: (error) => {
            console.error('[VoiceEngine] Opening TTS error:', error);
            setIsStarting(false);
            if (!fallbackStarted) { fallbackStarted = true; speakWithBrowserFallback(openingTextForUser || openingText); }
          },
        });
      }
    } catch (error) {
      console.error('[VoiceEngine] TTS failed:', error);
      if (!fallbackStarted) { fallbackStarted = true; speakWithBrowserFallback(openingTextForUser || openingText); }
    }
  }, [addToLog, questionsAsked, conversationLog, setQuestionsAsked, setConversationLog, speakWithPreferredProvider, echoFilter, useDeepgramFlux, useServerStt, startServerStt, setSpeaking, requestFirstQuestionAfterOpening, requestOpeningDisclaimer, resolveAssistantTextForUser]);

  const stopConversation = useCallback(() => {
    setIsActive(false);
    isActiveRef.current = false;
    setIsStarting(false);
    setIsListening(false);
    setSpeaking(false);
    hasStartedRef.current = false;
    isRecognitionRunningRef.current = false;
    setAudioConfirmed(false);
    audioConfirmedRef.current = false;

    pauseDetection.clearPauseTimer();
    isProcessingPauseRef.current = false;
    pendingDeferredTranscriptRef.current = '';
    deferredTranscriptOverrideRef.current = '';

    // Flush any pending user transcript into the log so "End Conversation" captures everything
    const pendingText = transcriptAccumulatorRef.current;
    transcriptAccumulatorRef.current = '';
    interimTranscriptRef.current = '';
    onLiveTranscriptRef.current?.('');
    if (pendingText && pendingText.trim()) {
      const cleaned = echoFilter.filterEcho(pendingText);
      if (cleaned && cleaned.trim()) {
        adapterRef.current.commitUserTurn(cleaned.trim());
      }
    }

    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
    }

    // Edge server-STT path: release mic + Deepgram socket.
    stopServerStt();

    if (isFluxConnected && deepgramFluxService.isConnected()) {
      deepgramFluxService.disconnect();
      setIsFluxConnected(false);
    }

    try {
      geminiFlashTTSService.stop();
    } catch (e) {
      /* ignore */
    }

    try {
      openAIStopSpeaking();
    } catch (e) {
      console.warn('[VoiceEngine] openAIStopSpeaking failed:', e);
    }

    try {
      deepgramTTSService.stop();
    } catch (e) {
      console.warn('[VoiceEngine] deepgramTTSService.stop failed:', e);
    }

    if (audioRef.current && typeof audioRef.current.pause === 'function') {
      audioRef.current.pause();
    }
    audioRef.current = null;

    onComplete?.(conversationLogRef.current);
  }, [isFluxConnected, onComplete, pauseDetection, setSpeaking, echoFilter, stopServerStt]);

  // Keep stopConversationRef in sync
  useEffect(() => { stopConversationRef.current = stopConversation; }, [stopConversation]);

  return {
    // State
    isActive,
    isStarting,
    isListening,
    isSpeaking,
    currentQuestion,
    questionsAsked,
    conversationLog,
    audioConfirmed,
    isAcceptingInput,
    speechSupported,
    isFluxConnected,
    endConversationAcknowledged,

    // Actions
    startConversation,
    stopConversation,
    askQuestion,
    addToLog,

    // Refs for UI
    conversationScrollRef,

    // Language
    language: symptomLanguage.language,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };
};

export default useVoiceConversationEngine;
